import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { EvaluateRulesResponse } from '@rossum/api-client/internal';
import { QueueRule } from '@rossum/api-client/schemas';
import DialogTitle from '@rossum/components/dialog-title/DialogTitle';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
} from '@rossum/ui/material';
import { useWorkspacesWithQueues } from '../../queues/hooks/useWorkspacesWithQueues';
import { QueueSelectList } from '../../queues/select-queue/QueueSelectList';
import { useMoveRule } from '../hooks/useMoveRule';
import {
  getUnavailableEmailTemplates,
  useReferencedEmailTemplates,
} from '../hooks/useReferencedEmailTemplates';
import { useValidateRuleAgainstQueues } from '../hooks/useValidateRuleAgainstQueues';
import { MoveToAnotherQueueValidationResult } from './MoveToAnotherQueueValidationResult';

export const MoveToAnotherQueueDialog = ({
  rule,
  open,
  onClose,
  onSuccess,
}: {
  rule: QueueRule | null;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}) => {
  const intl = useIntl();

  const [selectedQueue, setSelectedQueue] = useState<number | null>(null);

  const [validationErrors, setValidationErrors] = useState<
    EvaluateRulesResponse['messages']
  >([]);
  const [hasValidated, setHasValidated] = useState(false);

  const {
    workspacesWithQueues: queuesByWorkspace,
    isLoading: isQueuesLoading,
  } = useWorkspacesWithQueues({
    enableQueries: true,
  });

  const { mutate: validateRule, isLoading: isValidating } =
    useValidateRuleAgainstQueues();

  const { mutate: handleMoveRule, isLoading: isMoving } = useMoveRule(() => {
    onSuccess?.();
    onClose();
  });

  const referencedTemplates = useReferencedEmailTemplates(rule);

  const selectedQueueData = useMemo(() => {
    if (!queuesByWorkspace) return null;

    const queue = queuesByWorkspace
      .flatMap(workspace => workspace.queues)
      .find(q => q.id === selectedQueue);

    return queue ?? null;
  }, [selectedQueue, queuesByWorkspace]);

  const unavailableEmailTemplates = useMemo(
    () =>
      selectedQueueData
        ? getUnavailableEmailTemplates(
            referencedTemplates,
            selectedQueueData.url
          )
        : [],
    [referencedTemplates, selectedQueueData]
  );

  const onMoveRule = useCallback(() => {
    if (!selectedQueueData || !rule) return;
    handleMoveRule({ rule, targetQueue: selectedQueueData });
  }, [handleMoveRule, rule, selectedQueueData]);

  const handleValidateAndMove = () => {
    if (!selectedQueueData || !rule) return;

    validateRule(
      { rule, queues: [selectedQueueData.url] },
      {
        onSuccess: errorsPerQueue => {
          // I am pretty sure that there is only one queue in the list
          const errors = errorsPerQueue[0] ? errorsPerQueue[0].errors : [];

          setValidationErrors(errors);
          setHasValidated(true);

          if (errors.length === 0 && unavailableEmailTemplates.length === 0) {
            handleMoveRule({ rule, targetQueue: selectedQueueData });
          }
        },
      }
    );
  };

  const showValidationResult = useMemo(
    () =>
      validationErrors.length > 0 ||
      (hasValidated && unavailableEmailTemplates.length > 0),
    [validationErrors, hasValidated, unavailableEmailTemplates]
  );

  const handleCancelValidationResult = useCallback(() => {
    return showValidationResult
      ? () => {
          setValidationErrors([]);
          setHasValidated(false);
          setSelectedQueue(null);
        }
      : onClose;
  }, [onClose, showValidationResult]);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle
        title={
          validationErrors.length > 0
            ? intl.formatMessage({
                id: 'features.queueSettings.rules.list.moveToAnotherQueueDialog.validation.title',
              })
            : showValidationResult
              ? intl.formatMessage({
                  id: 'features.queueSettings.rules.list.moveToAnotherQueueDialog.emailTemplate.title',
                })
              : intl.formatMessage({
                  id: 'features.queueSettings.rules.list.moveToAnotherQueueDialog.title',
                })
        }
        onClose={onClose}
      />
      <DialogContent
        sx={{ mt: 2, height: '40vh', width: '500px', overflow: 'auto' }}
      >
        {showValidationResult && rule && selectedQueueData ? (
          <MoveToAnotherQueueValidationResult
            validationErrors={validationErrors}
            targetQueueName={selectedQueueData.name}
            unavailableEmailTemplates={unavailableEmailTemplates}
          />
        ) : (
          <QueueSelectList
            onSelect={queues => setSelectedQueue(queues[0] ?? null)}
            selectedQueues={selectedQueue ? [selectedQueue] : []}
            queuesByWorkspace={queuesByWorkspace || []}
            isLoading={isQueuesLoading}
            multiSelect={false}
            disabled={isQueuesLoading}
            autoFocus
          />
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button variant="outlined" onClick={handleCancelValidationResult()}>
          {intl.formatMessage({ id: 'components.confirmationDialog.cancel' })}
        </Button>
        {showValidationResult ? (
          <Button
            variant="contained"
            onClick={onMoveRule}
            disabled={isMoving}
            startIcon={isMoving ? <CircularProgress size={20} /> : null}
          >
            {intl.formatMessage({
              id: 'features.queueSettings.rules.list.moveToAnotherQueueDialog.moveAnyway',
            })}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleValidateAndMove}
            disabled={!selectedQueue || isValidating || isMoving}
            startIcon={
              isValidating || isMoving ? <CircularProgress size={20} /> : null
            }
          >
            {intl.formatMessage({
              id: 'features.queueSettings.rules.list.moveToAnotherQueueDialog.move',
            })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
